import {
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  ChevronUp,
} from "lucide-react";
import type { RefObject } from "react";

/**
 * Floating chat navigator. Six buttons stacked on the right edge of
 * the chat scroll area give the user fast jumps through long
 * conversations:
 *
 * <ul>
 *   <li>⏫ Top — first message</li>
 *   <li>⏶ Previous Q/A — skip backward to the previous turn boundary</li>
 *   <li>⬆ Page up — smooth scroll up by ~80% of the viewport</li>
 *   <li>⬇ Page down — smooth scroll down by ~80% of the viewport</li>
 *   <li>⏷ Next Q/A — skip forward to the next turn boundary</li>
 *   <li>⏬ Bottom — newest message</li>
 * </ul>
 *
 * <p>Turn boundaries are detected by querying the scroll container for
 * elements with {@code [data-turn-id]} — each {@code MessageCard} is
 * wrapped in such a div in {@code ChatPage}. Hidden when the chat has
 * fewer than 2 turns (nothing to navigate).
 */
export function ChatNavigator({
  scrollContainerRef,
  turnCount,
}: {
  scrollContainerRef: RefObject<HTMLDivElement>;
  turnCount: number;
}) {
  if (turnCount < 2) return null;

  function scrollContainer(): HTMLDivElement | null {
    return scrollContainerRef.current;
  }

  function scrollTo(y: number) {
    const c = scrollContainer();
    if (!c) return;
    c.scrollTo({ top: y, behavior: "smooth" });
  }

  function scrollBy(dy: number) {
    const c = scrollContainer();
    if (!c) return;
    c.scrollBy({ top: dy, behavior: "smooth" });
  }

  function turnTops(): number[] {
    const c = scrollContainer();
    if (!c) return [];
    const turns = c.querySelectorAll<HTMLElement>("[data-turn-id]");
    const containerTop = c.getBoundingClientRect().top;
    return Array.from(turns).map(
      (el) => el.getBoundingClientRect().top - containerTop + c.scrollTop,
    );
  }

  function jumpToPrevTurn() {
    const c = scrollContainer();
    if (!c) return;
    const tops = turnTops();
    if (tops.length === 0) return;
    // Use a small tolerance so the "current" turn (which is ~at scrollTop)
    // doesn't count as the previous one.
    const target = [...tops].reverse().find((t) => t < c.scrollTop - 8);
    scrollTo(target ?? 0);
  }

  function jumpToNextTurn() {
    const c = scrollContainer();
    if (!c) return;
    const tops = turnTops();
    if (tops.length === 0) return;
    const target = tops.find((t) => t > c.scrollTop + 8);
    scrollTo(target ?? c.scrollHeight);
  }

  function jumpToTop() {
    scrollTo(0);
  }

  function jumpToBottom() {
    const c = scrollContainer();
    if (!c) return;
    scrollTo(c.scrollHeight);
  }

  function pageUp() {
    const c = scrollContainer();
    if (!c) return;
    scrollBy(-Math.round(c.clientHeight * 0.8));
  }

  function pageDown() {
    const c = scrollContainer();
    if (!c) return;
    scrollBy(Math.round(c.clientHeight * 0.8));
  }

  return (
    <div
      className="pointer-events-none absolute right-3 top-1/2 z-10 -translate-y-1/2"
      aria-label="Chat navigation"
    >
      <div className="pointer-events-auto flex flex-col gap-1 rounded-full border border-datumcraft-slate/25 bg-white/95 p-1 shadow-card backdrop-blur">
        <NavBtn onClick={jumpToTop} title="Jump to top of conversation">
          <ChevronsUp size={15} aria-hidden />
        </NavBtn>
        <NavBtn onClick={jumpToPrevTurn} title="Previous question / answer">
          <ChevronUp size={15} aria-hidden />
        </NavBtn>
        <NavBtn onClick={pageUp} title="Scroll up (one screen)">
          <ChevronUp size={11} className="opacity-60" aria-hidden />
        </NavBtn>
        <span className="mx-auto h-px w-5 bg-datumcraft-slate/25" aria-hidden />
        <NavBtn onClick={pageDown} title="Scroll down (one screen)">
          <ChevronDown size={11} className="opacity-60" aria-hidden />
        </NavBtn>
        <NavBtn onClick={jumpToNextTurn} title="Next question / answer">
          <ChevronDown size={15} aria-hidden />
        </NavBtn>
        <NavBtn onClick={jumpToBottom} title="Jump to bottom (newest)">
          <ChevronsDown size={15} aria-hidden />
        </NavBtn>
      </div>
    </div>
  );
}

function NavBtn({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="inline-flex h-7 w-7 items-center justify-center rounded-full text-datumcraft-slate hover:bg-datumcraft-mist hover:text-datumcraft-navy"
    >
      {children}
    </button>
  );
}
